/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import moment from 'moment';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AggregationInterval, RlFlLinkUsage } from '../../../shared/entities/dataUsage';
import { selectUsageByLink } from '../../../store';
import { requestUsageByLink } from '../../../store/dataUsage/actions';
import { PlaceholderHeading, PlaceholderText, PlaceholderWrapper } from '../DataUsageChartsCommonComponents';
import DataUsageChartContainer from './DataUsageChartContainer';
import UsageLineChart from './UsageLineChart';

function translateChartData(data: { [sourceTime: number]: RlFlLinkUsage }, key: keyof RlFlLinkUsage) {
  return Object.keys(data)
    .sort()
    .map(timestamp => ({
      x: moment.unix(parseInt(timestamp)).toDate(),
      y: data[timestamp][key],
    }));
}

function getSeriesData(
  data: { [sourceTime: number]: RlFlLinkUsage } | undefined,
  key: keyof RlFlLinkUsage,
  name: string,
  shortenedName: string,
) {
  return {
    name,
    data: data ? translateChartData(data, key) : [],
    tooltipShortenedName: shortenedName,
  };
}

interface LinkUsageChartProps {
  width: number;
}
function LinkUsageChart({ width }: LinkUsageChartProps) {
  const dispatch = useDispatch();
  const usageByLink = useSelector(selectUsageByLink, shallowEqual);

  const series = [
    getSeriesData(usageByLink?.usageByLinkBytes, 'rlUsageBytes', 'Return Link', 'Return Link'),
    getSeriesData(usageByLink?.usageByLinkBytes, 'flUsageBytes', 'Forward Link', 'Forward Link'),
  ];

  return (
    <div id="linkUsageChart">
      <DataUsageChartContainer
        width={width}
        header="Link Usage"
        aggregationInterval={usageByLink?.aggregationInterval}
        menuItemClickHandler={(aggregationInterval: AggregationInterval) => {
          if (usageByLink) {
            dispatch(
              requestUsageByLink({
                fromMsSinceEpoch: usageByLink.fromMsSinceEpoch,
                toMsSinceEpoch: usageByLink.toMsSinceEpoch,
                datapathUuids: usageByLink.datapathUuids,
                aggregationInterval: aggregationInterval,
              }),
            );
          }
        }}
      >
        <UsageLineChart
          series={series}
          placeholder={
            <PlaceholderWrapper>
              <PlaceholderHeading>No Terminals</PlaceholderHeading>
              <PlaceholderText>To view data usage analytics, select one or multiple terminals</PlaceholderText>
            </PlaceholderWrapper>
          }
          showPlaceholder={usageByLink === undefined}
          aggregationInterval={usageByLink?.aggregationInterval}
          userEndDateMs={usageByLink?.toMsSinceEpoch}
          width={width}
        />
      </DataUsageChartContainer>
    </div>
  );
}

export default LinkUsageChart;
